import React, { useCallback, useEffect, useState } from 'react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import './PPADReport.css';
import Select2Dropdown from '../common/Select2Dropdown';
import TableScrollContainer from '../common/TableScrollContainer';
import JobForm from '../documentation/JobForm';
import { usePermissions } from '../../services/PermissionsContext'; // 1. Import
import useSortableTable from '../common/useSortableTable';

const API_BASE_URL = '/api/documentation/jobs/accepted-report/';

const ACCEPTED_COLOR = '#c2fcc2ff';
const SENT_COLOR = '#f3dcb8ff';



// UPDATED column definitions (add JOB ID and BL # right after S/N)
const ACCEPTED_COLUMNS = [
  { key: 'sn', label: 'S/N' },
  { key: 'job_id', label: 'JOB ID' },
  { key: 'bl_no', label: 'BL #' },
  { key: 'idf_no', label: 'IDF NO.' },
  { key: 'customer_name', label: 'CUSTOMER NAME' },
  { key: 'tansad_no', label: 'TANSAD NO.' },
  { key: 'date', label: 'DATE' },
  { key: 'username', label: 'USERNAME' },
  { key: 'sent', label: 'SENT' },
  { key: 'action', label: 'ACTION' },
  { key: 'reverse', label: 'P-PAD' },
];

const SENT_COLUMNS = [
  { key: 'sn', label: 'S/N' },
  { key: 'job_id', label: 'JOB ID' },
  { key: 'bl_no', label: 'BL #' },
  { key: 'idf_no', label: 'IDF NO.' },
  { key: 'customer_name', label: 'CUSTOMER NAME' },
  { key: 'tansad_no', label: 'TANSAD NO.' },
  { key: 'username', label: 'USERNAME' },
  { key: 'date', label: 'DATE' },
  { key: 'reverse', label: 'P-PAD' },
];

const FILTER_OPTIONS = [
  { id: 'ALL', text: 'ALL' },
  { id: 'ACCEPTED', text: 'ACCEPTED' },
  { id: 'SENT', text: 'SENT' },
];


const pad = n => String(n).padStart(2, '0');

const fmtReportDate = value => {
  if (!value) return '';
  const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(value);
  if (isDateOnly) {
    const [y, m, d] = value.split('-');
    return `${pad(d)}-${pad(m)}-${y}`;
  }
  const d = new Date(value);
  if (isNaN(d.getTime())) return '';
  return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

const parseReportDate = value => {
  if (!value) return null;
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d.getTime();
};

const customSortValue = (item, key) => key === 'date' ? parseReportDate(item[key]) : item[key];

const getAuthHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('kk_token')}`,
  'Content-Type': 'application/json'
});

const AcceptedReport = () => {
  const { hasPermission } = usePermissions(); // 2. Get the permission checker function
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState({});
  const [filter, setFilter] = useState('ALL');
  const [search, setSearch] = useState('');
  const [jobModal, setJobModal] = useState({
    open: false,
    job: null,
    documentsEditable: false,
    openDocumentsOnly: false
  });
  const [returnToPPadDialog, setReturnToPPadDialog] = useState({ show: false, jobId: null });

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    try {
      const term = search.trim();
      const response = await fetch(
        `${API_BASE_URL}${term ? `?search=${encodeURIComponent(term)}` : ''}`,
        { headers: getAuthHeaders() }
      );
      const result = await response.json();
      setJobs(Array.isArray(result) ? result : []);
    } catch (e) {
      setJobs([]);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    const timer = setTimeout(() => { fetchJobs(); }, search.trim() ? 350 : 0);
    return () => clearTimeout(timer);
  }, [fetchJobs, search]);


  // Filter jobs by status
  const acceptedJobs = jobs.filter(j => (j.status || '').toUpperCase() === 'ACCEPTED');
  const sentJobs = jobs.filter(j => (j.status || '').toUpperCase() === 'SENT');

  // Initialize useSortableTable for both tables
  const { items: sortedAcceptedJobs, requestSort: requestSortAccepted, getSortIndicator: getSortIndicatorAccepted } = useSortableTable(acceptedJobs, null, customSortValue);
  const { items: sortedSentJobs, requestSort: requestSortSent, getSortIndicator: getSortIndicatorSent } = useSortableTable(sentJobs, { key: 'date', direction: 'descending' }, customSortValue);

  // Filtered jobs for table (apply search filter)
  const filteredAcceptedJobs = sortedAcceptedJobs.filter(j => {
    if (!search) return true;
    return (j.idf_no || '').toLowerCase().includes(search.toLowerCase());
  });

  const filteredSentJobs = sortedSentJobs.filter(j => {
    if (!search) return true;
    return (j.idf_no || '').toLowerCase().includes(search.toLowerCase());
  });

  // Handle select/deselect
  const handleSelect = (jobId, checked) => {
    setSelected(prev => ({ ...prev, [jobId]: checked }));
  };

  const handleSelectAll = checked => {
    // Add a permission check
    if (!hasPermission('ACCEPTED ASSESSMENTS', 'can_edit')) return;
    const allIds = filteredAcceptedJobs.map(j => j.id);
    const newSelected = {};
    if (checked) allIds.forEach(id => { newSelected[id] = true; });
    setSelected(newSelected);
  };

  // Save (send) selected jobs
  const handleBulkSend = async () => {
    // Add a permission check
    if (!hasPermission('ACCEPTED ASSESSMENTS', 'can_edit')) return;
    const idsToSend = Object.keys(selected).filter(id => selected[id]);
    for (const jobId of idsToSend) {
      await fetch(`/api/documentation/jobs/${jobId}/update_assessment_status/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ status: 'SENT', date: new Date().toISOString().slice(0, 10) }),
      });
    }
    await fetchJobs();
    setSelected({});
  };

  const handleSend = async jobId => {
    // Add a permission check
    if (!hasPermission('ACCEPTED ASSESSMENTS', 'can_edit')) return;
    await fetch(`/api/documentation/jobs/${jobId}/update_assessment_status/`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ status: 'SENT', date: new Date().toISOString().slice(0, 10) }),
    });
    await fetchJobs();
    setSelected(prev => ({ ...prev, [jobId]: false }));
  };

  // Handle reverse status change
  const handleReverseClick = jobId => {
    if (!hasPermission('ACCEPTED ASSESSMENTS', 'can_edit')) return;
    setReturnToPPadDialog({ show: true, jobId });
  };

  const handleConfirmReturnToPPad = async () => {
    const { jobId } = returnToPPadDialog;
    if (!jobId) return;

    try {
      await fetch(`/api/documentation/jobs/${jobId}/update_assessment_status/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ return_to_ppad: true, date: new Date().toISOString().slice(0, 10) }),
      });
      await fetchJobs();
    } catch (e) {
      alert('Failed to send to P-PAD');
    }
    setReturnToPPadDialog({ show: false, jobId: null });
  };

  // Export to Excel
  const exportExcel = () => {
    const exportData = [];
    if (filter === 'ALL' || filter === 'ACCEPTED') {
      filteredAcceptedJobs.forEach((job, idx) => {
        exportData.push({
          'S/N': idx + 1,
          'JOB ID': job.id,
          'BL #': job.bl_value,
          'IDF NO': job.idf_no,
          'CUSTOMER NAME': job.customer_name,
          'TANSAD NO': job.tansad_no,
          'DATE': fmtReportDate(job.date),
          'USERNAME': job.username || '',
          'STATUS': job.status,
        });
      });
    }
    if (filter === 'ALL' || filter === 'SENT') {
      filteredSentJobs.forEach((job, idx) => {
        exportData.push({
          'S/N': idx + 1,
          'JOB ID': job.id,
          'BL #': job.bl_value,
          'IDF NO': job.idf_no,
          'CUSTOMER NAME': job.customer_name,
          'TANSAD NO': job.tansad_no,
          'DATE': fmtReportDate(job.date),
          'USERNAME': job.username || '',
          'STATUS': job.status,
        });
      });
    }
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Accepted Assessments Report");
    XLSX.writeFile(wb, "Accepted_Assessments_Report.xlsx");
  };

  // Export to PDF
  const exportPDF = () => {
    const doc = new jsPDF();
    doc.text("Accepted Assessments Report", 14, 10);
    const body = [];
    if (filter === 'ALL' || filter === 'ACCEPTED') {
      filteredAcceptedJobs.forEach((job, idx) => {
        body.push([
          idx + 1,
          job.id,
          job.bl_value,
          job.idf_no,
          job.customer_name,
          job.tansad_no,
          fmtReportDate(job.date),
          job.username || '',
          job.status,
        ]);
      });
    }
    if (filter === 'ALL' || filter === 'SENT') {
      filteredSentJobs.forEach((job, idx) => {
        body.push([
          idx + 1,
          job.id,
          job.bl_value,
          job.idf_no,
          job.customer_name,
          job.tansad_no,
          fmtReportDate(job.date),
          job.username || '',
          job.status,
        ]);
      });
    }
    doc.autoTable({
      head: [['S/N','JOB ID','BL #','IDF NO','CUSTOMER NAME','TANSAD NO','DATE','USERNAME','STATUS']],
      body,
      startY: 18,
      styles: { fontSize: 8, textTransform: 'uppercase' }
    });
    doc.save("Accepted_Assessments_Report.pdf");
  };

  const openJobModal = async (
    jobId,
    { documentsEditable = false, openDocumentsOnly = false, lockExistingDocuments = false } = {}
  ) => {
    try {
      const res = await fetch(`/api/documentation/jobs/${jobId}/`, { headers: getAuthHeaders() });
      if (!res.ok) {
        alert('Failed to load job details');
        return;
      }
      const data = await res.json();
      setJobModal({
        open: true,
        job: data,
        documentsEditable,
        openDocumentsOnly,
        lockExistingDocuments,
        openShipmentDetails: true,
      });
    } catch (e) {
      alert('Error loading job');
    }
  };

  const closeJobModal = () => {
    setJobModal({ open: false, job: null, documentsEditable: false, openDocumentsOnly: false });
  };

  // 3. Add a top-level permission check for viewing the report
  if (!hasPermission('ACCEPTED ASSESSMENTS', 'can_view')) {
    return (
        <div className="ppad-report-container">
            <h2>Permission Denied</h2>
            <p>You do not have permission to view this report.</p>
        </div>
    );
  }

  return (
    <div className="ppad-report-container" style={{ overflowX: 'auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <h2 style={{ textTransform: 'uppercase', margin: 0 }}>Accepted Assessments Report</h2>
        <button onClick={fetchJobs} title="Refresh" style={{ background: 'none', border: '1px solid transparent', borderRadius: 6, cursor: 'pointer', fontSize: 18, color: '#444', padding: '4px 8px', transition: 'background 0.15s, border-color 0.15s' }} onMouseEnter={e => { e.currentTarget.style.background = '#f0f0f0'; e.currentTarget.style.borderColor = '#bbb'; }} onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.borderColor = 'transparent'; }}><i className="fa-solid fa-rotate-right" style={{ fontWeight: 900 }} /></button>
      </div>
      <div className="toolbar" style={{ display: 'flex', gap: 16, marginBottom: 16, alignItems: 'center', flexWrap: 'nowrap', flexDirection: 'row' }}>
        <div style={{ width: 110, flexShrink: 0 }}>
          <Select2Dropdown
            options={FILTER_OPTIONS}
            value={filter}
            onChange={setFilter}
            displayKey="text"
            valueKey="id"
            placeholder="Filter by status"
            required={false}
            searchable={false}
            clearable={false}
          />
        </div>
        <input 
          type="text" 
          placeholder="Search IDF No..." 
          value={search} 
          onChange={(e) => setSearch(e.target.value)} 
          style={{ padding: '6px 8px', borderRadius: 4, border: '1px solid #ccc', fontSize: 13, minWidth: 200 }}
        />
        <button onClick={exportExcel} style={{ textTransform: 'uppercase', whiteSpace: 'nowrap' }}>Export Excel</button>
        <button onClick={exportPDF} style={{ textTransform: 'uppercase', whiteSpace: 'nowrap' }}>Export PDF</button>
      </div>
      {/* Accepted Section */}
      {(filter === 'ALL' || filter === 'ACCEPTED') && (
        <div style={{ marginBottom: 32 }}>
          <div
            style={{
              marginBottom: 8,
              background: ACCEPTED_COLOR,
              borderRadius: 6,
              padding: '8px 16px',
            }}
          >
            <span style={{ fontWeight: 'bold', fontSize: 16 }}>ACCEPTED</span>
          </div>
          <TableScrollContainer>
          <table style={{ width: '100%' }}>
            <thead>
                <tr>
                  {ACCEPTED_COLUMNS.map((col, idx) => {
                    const isSortable = col.key !== 'sent' && col.key !== 'action' && col.key !== 'reverse';
                    if (col.key === 'reverse' && !hasPermission('ACCEPTED ASSESSMENTS', 'can_edit')) return null;
                    return col.key === 'sent' ? (
                      <th
                        key={col.key}
                        style={{
                          width: '48px',
                          paddingLeft: 0,
                          paddingRight: 0,
                          textAlign: 'right'
                        }}
                      >
                        {col.label}
                        <input
                          type="checkbox"
                          style={{
                            marginRight: 0,
                            position: 'relative',
                            right: 0,
                          }}
                          checked={filteredAcceptedJobs.length > 0 && filteredAcceptedJobs.every(j => selected[j.id])}
                          onChange={e => handleSelectAll(e.target.checked)}
                          title="Select All"
                          disabled={!hasPermission('ACCEPTED ASSESSMENTS', 'can_edit')} // 4. Disable "Select All" checkbox
                        />
                      </th>
                    ) : (
                      <th 
                        key={col.key}
                        onClick={isSortable ? () => requestSortAccepted(col.key) : undefined}
                        style={isSortable ? { cursor: 'pointer' } : {}}
                      >
                        {col.label} {isSortable && getSortIndicatorAccepted(col.key)}
                      </th>
                    );
                  })}
                </tr>
            </thead>
            <tbody>
              {filteredAcceptedJobs.length === 0 ? (
                <tr><td colSpan={ACCEPTED_COLUMNS.filter(col => col.key !== 'reverse' || hasPermission('ACCEPTED ASSESSMENTS', 'can_edit')).length}>{loading ? 'LOADING...' : 'NO RECORDS FOUND.'}</td></tr>
              ) : filteredAcceptedJobs.map((job, idx) => (
                <tr key={job.id}>
                  <td>{idx + 1}</td>
                  <td>
                    {/* 5. Conditionally render link or plain text */}
                    {hasPermission('ACCEPTED ASSESSMENTS', 'can_edit') ? (
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          openJobModal(job.id, 
                            { documentsEditable: true, 
                              openDocumentsOnly: true,
                              lockExistingDocuments: true 
                            });
                        }}
                        title="Open Documents (Add new only)"
                        style={{ textDecoration: 'underline' }}
                      >
                        {job.id}
                      </a>
                    ) : (
                      <span>{job.id}</span>
                    )}
                  </td>
                  <td>{job.bl_value}</td>
                  <td>{job.idf_no}</td>
                  <td><strong>{job.customer_name}</strong></td>
                  <td>{job.tansad_no}</td>
                  <td>{fmtReportDate(job.date)}</td>
                  <td>{job.username || ''}</td>
                  <td style={{ width: '48px', paddingLeft: 0, paddingRight: 0, textAlign: 'right' }}>
                    <input
                      type="checkbox"
                      checked={!!selected[job.id]}
                      onChange={e => handleSelect(job.id, e.target.checked)}
                      style={{ marginRight: 0, position: 'relative', right: 0 }}
                      disabled={!hasPermission('ACCEPTED ASSESSMENTS', 'can_edit')} // 6. Disable individual checkbox
                    />
                  </td>
                  <td>
                    {/* 7. Conditionally render the Save button */}
                    {!!selected[job.id] && hasPermission('ACCEPTED ASSESSMENTS', 'can_edit') && (
                      <button
                        onClick={() => handleSend(job.id)}
                        style={{ textTransform: 'uppercase', fontSize: 12 }}
                      >
                        SAVE
                      </button>
                    )}
                  </td>
                  {hasPermission('ACCEPTED ASSESSMENTS', 'can_edit') && (
                    <td>
                      {job.return_to_ppad ? (
                        <i
                          className="fa fa-check"
                          style={{ color: 'green'}}
                          title="Sent to P-PAD"
                        />
                      ) : (
                        <button
                          onClick={() => handleReverseClick(job.id)}
                          className="icon-btn"
                          style={{ color: 'darkorange' }}
                          title="Send to P-PAD"
                        >
                          <i className="fa fa-undo"></i>
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          </TableScrollContainer>
          {/* 8. Conditionally render the bulk send button */}
          {filteredAcceptedJobs.length > 0 && hasPermission('ACCEPTED ASSESSMENTS', 'can_edit') && (
            <div style={{ marginTop: 12, textAlign: 'right' }}>
              <button
                onClick={handleBulkSend}
                style={{
                  textTransform: 'uppercase',
                  background: '#4caf50',
                  color: '#fff',
                  padding: '6px 16px',
                  borderRadius: 4,
                  fontWeight: 'bold',
                  fontSize: 13
                }}
                disabled={Object.keys(selected).filter(id => selected[id]).length === 0}
              >
                SELECTED SENT
              </button>
            </div>
          )}
        </div>
      )}
      {/* Sent Assessment Section */}
      {(filter === 'ALL' || filter === 'SENT') && (
        <div>
          <div
            style={{
              marginBottom: 8,
              background: SENT_COLOR,
              borderRadius: 6,
              padding: '8px 16px',
            }}
          >
            <span style={{ fontWeight: 'bold', fontSize: 16 }}>SENT</span>
          </div>
          <TableScrollContainer>
          <table style={{ width: '100%' }}>
            <thead>
              <tr>
                {SENT_COLUMNS.map(col => {
                  if (col.key === 'reverse' && !hasPermission('ACCEPTED ASSESSMENTS', 'can_edit')) return null;
                  return (
                    <th 
                      key={col.key}
                      onClick={() => requestSortSent(col.key)}
                      style={{ cursor: 'pointer' }}
                    >
                      {col.label} {getSortIndicatorSent(col.key)}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {filteredSentJobs.length === 0 ? (
                <tr><td colSpan={SENT_COLUMNS.filter(col => col.key !== 'reverse' || hasPermission('ACCEPTED ASSESSMENTS', 'can_edit')).length}>{loading ? 'LOADING...' : 'NO RECORDS FOUND.'}</td></tr>
              ) : filteredSentJobs.map((job, idx) => (
                <tr key={job.id}>
                  <td>{idx + 1}</td>
                  <td>{job.id}</td>
                  <td>{job.bl_value}</td>
                  <td>{job.idf_no}</td>
                  <td><strong>{job.customer_name}</strong></td>
                  <td>{job.tansad_no}</td>
                  <td>{job.username || ''}</td>
                  <td>{fmtReportDate(job.date)}</td>
                  {hasPermission('ACCEPTED ASSESSMENTS', 'can_edit') && (
                    <td>
                      {job.return_to_ppad ? (
                        <i
                          className="fa fa-check"
                          style={{ color: 'green', fontSize: 16 }}
                          title="Sent to P-PAD"
                        />
                      ) : (
                        <button
                          onClick={() => handleReverseClick(job.id)}
                          className="icon-btn"
                          style={{ color: 'darkorange' }}
                          title="Send to P-PAD"
                        >
                          <i className="fa fa-undo"></i>
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          </TableScrollContainer>
        </div>
      )}
      {jobModal.open && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.4)',
            zIndex: 11000,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            overflowY: 'auto',
            padding: '40px 20px'
          }}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: 8,
              width: '95%',
              maxWidth: 1400,
              boxShadow: '0 6px 28px rgba(0,0,0,0.25)',
              position: 'relative'
            }}
          >
            <button
              onClick={() => closeJobModal()}
              style={{
                position: 'absolute',
                top: 8,
                right: 8,
                background: '#eee',
                border: 'none',
                borderRadius: '50%',
                width: 32,
                height: 32,
                cursor: 'pointer',
                fontSize: 16,
                fontWeight: 'bold'
              }}
              title="Close"
            >
              ×
            </button>
            {jobModal.job ? (
              <JobForm
                mode="view"
                initialData={jobModal.job}
                onClose={() => closeJobModal()}
                documentsEditable={jobModal.documentsEditable}
                openDocumentsOnly={jobModal.openDocumentsOnly}
                lockExistingDocuments={jobModal.lockExistingDocuments}
                openShipmentDetails={jobModal.openShipmentDetails}
                hideEmailSection={true}
                showEmailButtonOnly={true}
              />
            ) : (
              <div style={{ padding: 40 }}>Loading...</div>
            )}
          </div>
        </div>
      )}
      {/* Confirm Return to P-PAD Dialog */}
      {returnToPPadDialog.show && (
        <div className="modal-overlay">
          <div className="dialog-container" style={{ width: '520px', maxWidth: '95%' }}>
            <div className="dialog-header" style={{ background: '#0d6efd', color: '#fff', borderRadius: '4px 4px 0 0' }}>
              <h3 style={{ margin: 0, color: '#fff' }}>Send to P-PAD</h3>
            </div>
            <div className="dialog-body" style={{ padding: '20px' }}>
              <p style={{ marginBottom: '10px', fontSize: '1rem' }}>
                Are you sure you want to send this to P-PAD?
              </p>
            </div>
            <div className="dialog-footer">
              <button className="dialog-btn dialog-btn-primary" onClick={handleConfirmReturnToPPad}>
                Yes, Send to P-PAD
              </button>
              <button className="dialog-btn dialog-btn-secondary" onClick={() => setReturnToPPadDialog({ show: false, jobId: null })}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AcceptedReport;